<template>
  <div class="formats-tab">
    <nav class="formats-tab__nav" role="tablist">
      <button
        v-for="format in formats"
        :key="format.value"
        type="button"
        role="tab"
        class="formats-tab__nav-item"
        :class="{ _active: format.value === current.value }"
        :aria-selected="format.value === current.value"
        @click="$emit('select', format.value)"
      >
        <span class="formats-tab__nav-name">
          {{ format.name }}
          <span v-if="marks[format.value]" class="sat-dot" :class="`_${marks[format.value]}`" :title="$t(`platform.card.marks.${marks[format.value]}`)"></span>
        </span>
        <span class="formats-tab__nav-status" :class="{ _on: statuses[format.title] }">
          {{ statuses[format.title] ? $t("platform.card.formats.on") : $t("platform.card.formats.off") }}
        </span>
      </button>
    </nav>

    <SettingsSection class="formats-tab__body" :title="current.name" :description="$t(`platform.card.formats.about.${current.title}`)">
      <template #actions>
        <button type="button" class="btn _border formats-tab__faq" @click="openFaq">
          {{ $t("platform.settings.more_about_format") }}
        </button>
      </template>

      <div class="formats-tab__toggle">
        <CheckBoxWrapper :key="`${current.value}-${toggleRender}`" :value="Boolean(statuses[current.title])" :disabled="toggling" :text="$t(statuses[current.title] ? 'platform.card.formats.enabled' : 'platform.card.formats.disabled')" @input="toggle(current)" />
        <p class="form-item__hint mt-2 mb-0">{{ $t("platform.card.formats.enabled_hint") }}</p>
      </div>
      <div v-if="statusesLoaded && !statuses[current.title] && current.settings" class="sat-note _warn mb-4">
        <SatIcon name="info" :size="16" />
        <span>{{ $t("platform.card.formats.disabled_note") }}</span>
      </div>

      <div v-if="!current.settings" class="sat-note">
        <SatIcon name="info" :size="16" />
        <span>{{ $t("platform.card.formats.no_settings") }}</span>
      </div>
      <div v-else class="row">
        <keep-alive>
          <component :is="current.value" :key="current.value" />
        </keep-alive>
      </div>
    </SettingsSection>
  </div>
</template>
<script>
import { mapGetters } from "vuex";
import siteSettingsService from "@/api/siteSettingsService";
import { showSettingsSavedAlert } from "@/helpers/selfHostedScript";
import CheckBoxWrapper from "@/components/CheckBoxWrapper.vue";
import SatIcon from "@/components/Satellites/SatIcon.vue";
import SettingsSection from "@/components/PlatformCard/SettingsSection.vue";
import NativeWebPush from "@/components/PlatformSettings/Blocks/NativeWebPush.vue";
import InPage from "@/components/PlatformSettings/Blocks/InPage.vue";
import OnClick from "@/components/PlatformSettings/Blocks/OnClick.vue";
import ClickPush from "@/components/PlatformSettings/Blocks/ClickPush.vue";
import ZeroClick from "@/components/PlatformSettings/Blocks/ZeroClick.vue";

// `name` is also what POST settings-status takes as the format, `title` is the key of GET settings-status.
// `settings`: the format has a settings block (the component named `value`); Back Button is only turned on or off.
const FORMATS = [
  { name: "Native Web Push", value: "NativeWebPush", title: "native_web_push", faq: "push", settings: true },
  { name: "InPage", value: "InPage", title: "in_page", faq: "inpage", settings: true },
  { name: "OnClick", value: "OnClick", title: "on_click", faq: "onclick", settings: true },
  { name: "2Click Push", value: "ClickPush", title: "two_click_push", faq: "general_questions", flag: "2clickpush", settings: true },
  { name: "0Click Push", value: "ZeroClick", title: "zero_click_push", faq: "general_questions", settings: true },
  { name: "Back Button", value: "BackButton", title: "back_button", faq: "general_questions" },
];

// The ad formats of a site or landing: which are on, and the settings of each. A format block keeps its changes
// while another one is open (keep-alive), the card saves them all.
export default {
  name: "FormatsTab",
  components: { CheckBoxWrapper, SatIcon, SettingsSection, NativeWebPush, InPage, OnClick, ClickPush, ZeroClick },
  props: {
    platformId: {
      type: [Number, String],
      required: true,
    },
    // The open format (its `value`); the first one when empty or unknown
    sub: {
      type: String,
      default: "",
    },
    // { [format value]: "warn" | "danger" }
    marks: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      statuses: {},
      statusesLoaded: false,
      toggling: false,
      // Re-renders the switch when a toggle fails, so it shows the saved state again
      toggleRender: 0,
    };
  },
  computed: {
    ...mapGetters({
      featureFlags: "user/getFeatureFlags",
    }),
    formats() {
      return FORMATS.filter((format) => !format.flag || (this.featureFlags && this.featureFlags.includes(format.flag)));
    },
    current() {
      return this.formats.find((format) => format.value === this.sub) || this.formats[0];
    },
  },
  watch: {
    // On narrow screens the formats scroll sideways: keep the open one in sight
    "current.value": {
      immediate: true,
      handler() {
        this.$nextTick(() => {
          const active = this.$el && this.$el.querySelector(".formats-tab__nav-item._active");
          if (active) active.scrollIntoView({ block: "nearest", inline: "nearest" });
        });
      },
    },
  },
  created() {
    siteSettingsService.getFormats(this.platformId).then((res) => {
      if (res && res.status === 200) {
        this.statuses = { ...res.data };
      }
      this.statusesLoaded = true;
    });
  },
  methods: {
    openFaq() {
      this.$router.push({ name: "FAQ", query: { format: this.current.faq } });
    },
    // Turning a format on or off is saved at once: it is not part of the settings the save bar sends
    toggle(format) {
      const enabled = !this.statuses[format.title];
      this.toggling = true;
      siteSettingsService
        .setSubAccountFormat(this.platformId, { title: format.name, enabled })
        .then((res) => {
          if (res && res.status === 200) {
            this.$set(this.statuses, format.title, enabled);
            showSettingsSavedAlert(this, this.platformId);
            return;
          }
          this.toggleRender++;
          this.$alert({
            title: this.$t("sent_error"),
            text: (res && res.message) || this.$t("server_error"),
          });
        })
        .finally(() => {
          this.toggling = false;
        });
    },
  },
};
</script>
<style lang="scss" scoped>
.formats-tab {
  display: grid;
  grid-template-columns: 230px minmax(0, 1fr);
  gap: 20px;
  align-items: start;

  @media (max-width: 1199px) {
    grid-template-columns: minmax(0, 1fr);
  }
}

.formats-tab__nav {
  position: sticky;
  top: 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;

  @media (max-width: 1199px) {
    position: static;
    flex-direction: row;
    overflow-x: auto;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }
}

.formats-tab__nav-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 12px 14px;
  border: 1px solid var(--sat-border);
  border-radius: 12px;
  text-align: left;
  font-size: 15px;
  font-weight: 600;
  color: var(--sat-text);
  background: var(--sat-card);
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;

  &:hover {
    background: var(--sat-hover);
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    box-shadow: 0 0 0 2px var(--sat-accent);
  }

  &._active {
    border-color: var(--sat-accent);
    background: var(--sat-accent-soft);
  }

  @media (max-width: 1199px) {
    flex: 0 0 auto;
  }
}

.formats-tab__nav-name {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
}

.formats-tab__nav-status {
  flex-shrink: 0;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  color: var(--sat-muted);
  background: var(--sat-surface);

  &._on {
    color: var(--sat-ok);
    background: var(--sat-ok-soft);
  }
}

.formats-tab__body {
  min-width: 0;
}

.formats-tab__faq {
  height: 40px;
}

.formats-tab__toggle {
  margin-bottom: 20px;
}
</style>
